import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientsForCountyMap, getZoneFromStatus, lastFirst, getLeads } from '../../lib/supabase';
import { StatTile } from '../../components/StatTile';
import { colorForCount as colorForValue } from '../../lib/countyMapUtil';
import { loadApprovedCounties } from '../../lib/serviceArea';
import countyGeo from '../../lib/gaCountyGeo.json';

// Zone -> the same badge-* classes Pipeline.js already uses for these statuses,
// so a client's status pill reads identically here and on the pipeline board.
const ZONE_BADGE_CLASS = {
  Outreach: 'badge-marketing',
  'Clinical Intake': 'badge-processing',
  Authorization: 'badge-approval',
  Active: 'badge-active',
  Inactive: 'badge-closed',
};

const countyByLowerName = new Map(countyGeo.counties.map(c => [c.name.toLowerCase(), c]));

const ZOOM_MIN = 1;
const ZOOM_MAX = 7;

export default function ServiceCountyMap() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [leadRows, setLeadRows] = useState([]);
  const [showLeads, setShowLeads] = useState(true);
  const [mode, setMode] = useState('active'); // 'active' | 'all'
  const [query, setQuery] = useState('');
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [tip, setTip] = useState(null); // { x, y, name, seat, value, leadValue }
  // The approved area, from Settings → Approved Counties (lib/serviceArea.js).
  const [approvedList, setApprovedList] = useState([]);
  const approvedLower = useMemo(() => new Set(approvedList.map(c => c.toLowerCase())), [approvedList]);

  const stageRef = useRef(null);
  const zoomLayerRef = useRef(null);
  const zoomState = useRef({ scale: 1, panX: 0, panY: 0 });
  const dragRef = useRef({ dragging: false, moved: false, lastX: 0, lastY: 0 });
  const pinchRef = useRef({ dist: 0 });

  useEffect(() => {
    let cancelled = false;
    Promise.all([getClientsForCountyMap(), getLeads(), loadApprovedCounties()]).then(([clientsRes, leadsRes, approvedRes]) => {
      if (cancelled) return;
      setApprovedList(approvedRes.data || []);
      if (clientsRes.error) { setError(clientsRes.error.message || 'Could not load clients.'); setLoading(false); return; }
      setRows(clientsRes.data || []);
      setLeadRows(leadsRes.data || []); // leads failing to load shouldn't block the client map
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Group clients by canonical GA county name. County text on the client record
  // is matched case/whitespace-insensitively against the real county list —
  // anything that doesn't match a real county is surfaced, not silently dropped.
  const { countyData, unassignedCount, unmatched, totals } = useMemo(() => {
    const map = {};
    let unassigned = 0;
    const unmatchedList = [];
    rows.forEach(r => {
      const raw = (r.county || '').trim();
      if (!raw) { unassigned++; return; }
      const hit = countyByLowerName.get(raw.toLowerCase());
      if (!hit) { unmatchedList.push(r); return; }
      const zone = getZoneFromStatus(r.current_status);
      if (!map[hit.name]) map[hit.name] = { total: 0, active: 0, clients: [] };
      map[hit.name].total++;
      if (zone === 'Active') map[hit.name].active++;
      map[hit.name].clients.push({
        id: r.id,
        client_id: r.client_id,
        name: lastFirst(r.first_name, r.last_name),
        status: r.current_status,
        zone,
      });
    });
    const all = { all: 0, active: 0 };
    Object.values(map).forEach(cd => { all.all += cd.total; all.active += cd.active; });
    return { countyData: map, unassignedCount: unassigned, unmatched: unmatchedList, totals: all };
  }, [rows]);

  // Leads by county — open/active pipeline leads only (getLeads' own default
  // excludes every closed status — Not Interested/Unreachable/Duplicate/
  // Deceased/Ineligible/Moved/Chose Another Provider/Other/Converted),
  // matched the same way as clients. No active/all split here — a lead
  // doesn't have that axis.
  const { leadCountyData, unmatchedLeads } = useMemo(() => {
    const map = {};
    const unmatchedList = [];
    leadRows.forEach(l => {
      const raw = (l.county || '').trim();
      if (!raw) return;
      const hit = countyByLowerName.get(raw.toLowerCase());
      if (!hit) { unmatchedList.push(l); return; }
      if (!map[hit.name]) map[hit.name] = { total: 0, leads: [] };
      map[hit.name].total++;
      map[hit.name].leads.push({
        id: l.id,
        lead_id: l.lead_id,
        name: lastFirst(l.first_name, l.last_name),
        status: l.current_status,
      });
    });
    return { leadCountyData: map, unmatchedLeads: unmatchedList };
  }, [leadRows]);

  const leadValueFor = useCallback((countyName) => (showLeads ? (leadCountyData[countyName]?.total || 0) : 0), [leadCountyData, showLeads]);

  const valueFor = useCallback((countyName) => {
    const cd = countyData[countyName];
    if (!cd) return 0;
    return mode === 'active' ? cd.active : cd.total;
  }, [countyData, mode]);

  const maxValue = useMemo(() => {
    let m = 1;
    countyGeo.counties.forEach(c => { m = Math.max(m, valueFor(c.name)); });
    return m;
  }, [valueFor]);

  const q = query.trim().toLowerCase();
  const countiesWithClients = countyGeo.counties.filter(c => valueFor(c.name) > 0).length;
  const countiesWithLeads = countyGeo.counties.filter(c => leadValueFor(c.name) > 0).length;

  // ── Zoom / pan (imperative — avoid a React re-render on every wheel tick) ──
  const applyTransform = () => {
    const { scale, panX, panY } = zoomState.current;
    if (zoomLayerRef.current) {
      zoomLayerRef.current.style.transform = `translate(${panX}px,${panY}px) scale(${scale})`;
    }
  };
  const clampPan = () => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const { scale } = zoomState.current;
    const w = rect.width * scale, h = rect.height * scale;
    const minX = Math.min(0, rect.width - w), minY = Math.min(0, rect.height - h);
    zoomState.current.panX = Math.min(0, Math.max(minX, zoomState.current.panX));
    zoomState.current.panY = Math.min(0, Math.max(minY, zoomState.current.panY));
    if (scale === 1) { zoomState.current.panX = 0; zoomState.current.panY = 0; }
  };
  const zoomAt = (clientX, clientY, factor) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const mx = clientX - rect.left, my = clientY - rect.top;
    const { scale, panX, panY } = zoomState.current;
    const newScale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale * factor));
    const ratio = newScale / scale;
    zoomState.current.panX = mx - (mx - panX) * ratio;
    zoomState.current.panY = my - (my - panY) * ratio;
    zoomState.current.scale = newScale;
    clampPan();
    applyTransform();
  };
  const resetZoom = () => { zoomState.current = { scale: 1, panX: 0, panY: 0 }; applyTransform(); };

  const onWheel = (e) => {
    e.preventDefault();
    const factor = Math.pow(1.0015, -e.deltaY);
    zoomAt(e.clientX, e.clientY, factor);
  };
  const onMouseDown = (e) => {
    if (e.target.closest('[data-nozoom]')) return;
    e.preventDefault(); // avoid selecting map/county-name text while dragging
    dragRef.current = { dragging: true, moved: false, lastX: e.clientX, lastY: e.clientY };
  };
  // Mousemove/mouseup live on `window`, not the stage div — a drag toward the
  // edge of the visible area (the whole point of panning a zoomed-in map)
  // naturally carries the cursor outside the div's bounds. Listening on the
  // div and cancelling on mouseleave killed the drag exactly when the user
  // tried to pan far enough to reach content off to the side.
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.dragging) return;
      const dx = e.clientX - d.lastX, dy = e.clientY - d.lastY;
      if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true;
      zoomState.current.panX += dx; zoomState.current.panY += dy;
      d.lastX = e.clientX; d.lastY = e.clientY;
      clampPan(); applyTransform();
    };
    const onUp = () => { dragRef.current.dragging = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const touchDist = (touches) => {
    const [a, b] = touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      dragRef.current = { dragging: true, moved: false, lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      dragRef.current.dragging = false;
      pinchRef.current.dist = touchDist(e.touches);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const d = touchDist(e.touches);
      const factor = d / (pinchRef.current.dist || d);
      pinchRef.current.dist = d;
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      zoomAt(midX, midY, factor);
    } else if (e.touches.length === 1 && dragRef.current.dragging) {
      const t = e.touches[0];
      const d = dragRef.current;
      const dx = t.clientX - d.lastX, dy = t.clientY - d.lastY;
      if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true;
      zoomState.current.panX += dx; zoomState.current.panY += dy;
      d.lastX = t.clientX; d.lastY = t.clientY;
      clampPan(); applyTransform();
    }
  };
  const onTouchEnd = () => { dragRef.current.dragging = false; };

  const showTip = (e, c, v) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, name: c.name, seat: c.seat, value: v, leadValue: leadValueFor(c.name) });
  };
  const hideTip = () => setTip(null);

  // Plain open — for anything outside the pannable map layer (the approved-
  // counties chip list), which can never be "dragged" and should just open.
  const openCounty = (name) => setSelectedCounty(name);
  // Drag-aware open — for county paths/badges inside the zoom/pan layer,
  // where a click can be the tail end of a drag gesture and must be
  // suppressed. Scoped to those two call sites only, so a stale `moved`
  // flag from panning the map can never block a click on an unrelated
  // element (e.g. a chip) elsewhere on the page.
  const openCountyFromMap = (name) => {
    if (dragRef.current.moved) { dragRef.current.moved = false; return; }
    setSelectedCounty(name);
  };
  const closeDrawer = () => setSelectedCounty(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const selected = selectedCounty ? countyData[selectedCounty] : null;
  const selectedGeo = selectedCounty ? countyByLowerName.get(selectedCounty.toLowerCase()) : null;
  const selectedList = selected
    ? (mode === 'active' ? selected.clients.filter(c => c.zone === 'Active') : selected.clients)
    : [];
  const selectedLeads = selectedCounty && showLeads ? (leadCountyData[selectedCounty]?.leads || []) : [];

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text3)', fontSize: 13 }}>Loading client locations…</div>;
  }

  return (
    <div>
      {error && (
        <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(108px,1fr))', gap: 8 }}>
          <StatTile label={mode === 'active' ? 'Active clients' : 'Clients mapped'} value={mode === 'active' ? totals.active : totals.all} />
          <StatTile label="Counties served" value={Object.keys(countyData).length} />
          <StatTile label="Unassigned county" value={unassignedCount} tone={unassignedCount > 0 ? 'warn' : 'neutral'} />
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 2 }}>
            {[{ key: 'active', label: 'Active only' }, { key: 'all', label: 'All clients' }].map(m => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                style={{
                  border: 'none', borderRadius: 6, padding: '6px 12px', fontFamily: 'var(--font)',
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                  background: mode === m.key ? 'var(--card-bg)' : 'transparent',
                  color: mode === m.key ? 'var(--text)' : 'var(--text2)',
                  boxShadow: mode === m.key ? '0 1px 2px rgba(10,22,40,0.08)' : 'none',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a county…"
            style={{
              fontFamily: 'var(--font)', fontSize: 13, padding: '7px 10px', borderRadius: 8,
              border: '0.5px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)', width: 180,
            }}
          />
          <button
            onClick={() => setShowLeads(s => !s)}
            title={showLeads ? 'Hide leads' : 'Show leads'}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font)', fontSize: 12.5, fontWeight: 600,
              padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
              background: showLeads ? 'var(--lead-marker-bg)' : 'var(--card-bg)',
              color: showLeads ? 'var(--lead-marker)' : 'var(--text2)',
              border: `0.5px solid ${showLeads ? 'var(--lead-marker-border)' : 'var(--border)'}`,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 2, background: showLeads ? 'var(--lead-marker)' : 'var(--border2)' }} />
            Leads {leadRows.length > 0 && `(${leadRows.length})`}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            159 counties · {countiesWithClients} with {mode === 'active' ? 'active ' : ''}clients
            {showLeads && countiesWithLeads > 0 && ` · ${countiesWithLeads} with leads`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>Fewer</span>
              <span style={{ display: 'flex', height: 10, width: 90, borderRadius: 3, overflow: 'hidden', border: '0.5px solid var(--border)' }}>
                {[0, 1, 2, 3, 4].map(i => (
                  <span key={i} style={{ flex: 1, background: colorForValue((i / 4) * maxValue || 0.001, maxValue) }} />
                ))}
              </span>
              <span>More</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 16, height: 10, borderRadius: 3, border: '2px solid var(--success)', boxSizing: 'border-box' }} />
              <span>Approved service area</span>
            </div>
            {showLeads && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--lead-marker)' }} />
                <span>Lead</span>
              </div>
            )}
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div
            ref={stageRef}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ position: 'relative', width: '100%', overflow: 'hidden', touchAction: 'none', background: 'var(--bg)', cursor: 'grab' }}
          >
            <div ref={zoomLayerRef} style={{ position: 'relative', transformOrigin: '0 0', willChange: 'transform' }}>
              <svg viewBox={`0 0 ${countyGeo.width} ${countyGeo.height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
                {countyGeo.counties.map(c => {
                  const v = valueFor(c.name);
                  const approved = approvedLower.has(c.name.toLowerCase());
                  const clickable = v > 0 || approved || leadValueFor(c.name) > 0;
                  const dim = q && !c.name.toLowerCase().includes(q);
                  return (
                    <path
                      key={c.name}
                      d={c.d}
                      fill={colorForValue(v, maxValue)}
                      stroke="var(--card-bg)"
                      strokeWidth={1}
                      opacity={dim ? 0.28 : 1}
                      style={{ cursor: clickable ? 'pointer' : 'default', transition: 'filter 0.12s ease' }}
                      onMouseMove={(e) => showTip(e, c, v)}
                      onMouseLeave={hideTip}
                      onClick={() => clickable && openCountyFromMap(c.name)}
                    />
                  );
                })}
                {/* Approved-service-area outline — a separate visual channel from the
                    count-based fill above, so "approved" and "client density" never
                    get conflated on the same color. */}
                {countyGeo.counties.filter(c => approvedLower.has(c.name.toLowerCase())).map(c => (
                  <path
                    key={`approved-${c.name}`}
                    d={c.d}
                    fill="none"
                    stroke="var(--success)"
                    strokeWidth={2.5}
                    strokeLinejoin="round"
                    pointerEvents="none"
                    opacity={q && !c.name.toLowerCase().includes(q) ? 0.28 : 0.9}
                  />
                ))}
              </svg>
              {countyGeo.counties.map(c => {
                const v = valueFor(c.name);
                if (v <= 0) return null;
                const dim = q && !c.name.toLowerCase().includes(q);
                return (
                  <button
                    key={c.name}
                    data-nozoom
                    onClick={(e) => { e.stopPropagation(); openCountyFromMap(c.name); }}
                    onMouseMove={(e) => showTip(e, c, v)}
                    onMouseLeave={hideTip}
                    title={`${c.name} County`}
                    style={{
                      position: 'absolute', left: `${(c.cx / countyGeo.width) * 100}%`, top: `${(c.cy / countyGeo.height) * 100}%`,
                      transform: `translate(-50%,-50%)${selectedCounty === c.name ? ' scale(1.12)' : ''}`,
                      minWidth: 24, height: 24, padding: '0 6px', borderRadius: 999,
                      background: selectedCounty === c.name ? 'var(--copper)' : 'var(--accent2)',
                      color: '#fff', fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      border: '2px solid var(--card-bg)', boxShadow: '0 2px 6px rgba(10,22,40,0.28)',
                      cursor: 'pointer', opacity: dim ? 0.25 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {v}
                  </button>
                );
              })}
              {/* Lead markers — a distinct shape (rounded square) as well as color,
                  offset up-left from the client badge, so the two series read
                  apart even for a viewer who can't distinguish the hues. */}
              {showLeads && countyGeo.counties.map(c => {
                const lv = leadValueFor(c.name);
                if (lv <= 0) return null;
                const hasClientBadge = valueFor(c.name) > 0;
                const dim = q && !c.name.toLowerCase().includes(q);
                return (
                  <button
                    key={`lead-${c.name}`}
                    data-nozoom
                    onClick={(e) => { e.stopPropagation(); openCountyFromMap(c.name); }}
                    onMouseMove={(e) => showTip(e, c, valueFor(c.name))}
                    onMouseLeave={hideTip}
                    title={`${c.name} County — ${lv} lead${lv === 1 ? '' : 's'}`}
                    style={{
                      position: 'absolute',
                      left: `${(c.cx / countyGeo.width) * 100}%`,
                      top: `${(c.cy / countyGeo.height) * 100}%`,
                      transform: hasClientBadge ? 'translate(-135%,-135%)' : 'translate(-50%,-50%)',
                      minWidth: 20, height: 20, padding: '0 5px', borderRadius: 6,
                      background: 'var(--lead-marker)',
                      color: '#fff', fontSize: 10.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      border: '2px solid var(--card-bg)', boxShadow: '0 2px 6px rgba(10,22,40,0.28)',
                      cursor: 'pointer', opacity: dim ? 0.25 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {lv}
                  </button>
                );
              })}
            </div>

            {tip && (
              <div style={{
                position: 'absolute', left: tip.x, top: tip.y, transform: 'translate(-50%,-120%)',
                background: 'var(--navy, #0A1628)', color: '#fff', fontSize: 12, padding: '7px 10px',
                borderRadius: 7, boxShadow: '0 6px 18px rgba(0,0,0,0.3)', whiteSpace: 'nowrap',
                pointerEvents: 'none', zIndex: 5,
              }}>
                <b>{tip.name} County</b><br />
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Seat: {tip.seat || '—'}</span>
                <br />{tip.value > 0 ? `${tip.value} client${tip.value === 1 ? '' : 's'}` : 'No clients on file'}
                {tip.leadValue > 0 && <><br /><span style={{ color: 'var(--lead-marker)' }}>{tip.leadValue} lead{tip.leadValue === 1 ? '' : 's'}</span></>}
              </div>
            )}

            <div data-nozoom style={{ position: 'absolute', right: 10, bottom: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button onClick={() => zoomAt(stageRef.current.getBoundingClientRect().left + stageRef.current.getBoundingClientRect().width / 2, stageRef.current.getBoundingClientRect().top + stageRef.current.getBoundingClientRect().height / 2, 1.5)}
                style={{ width: 28, height: 28, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text2)', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>+</button>
              <button onClick={() => zoomAt(stageRef.current.getBoundingClientRect().left + stageRef.current.getBoundingClientRect().width / 2, stageRef.current.getBoundingClientRect().top + stageRef.current.getBoundingClientRect().height / 2, 1 / 1.5)}
                style={{ width: 28, height: 28, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text2)', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>−</button>
              <button onClick={resetZoom} title="Reset zoom"
                style={{ width: 28, height: 28, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>⟲</button>
            </div>
            <div data-nozoom style={{ position: 'absolute', left: 10, bottom: 10, fontSize: 10.5, color: 'var(--text3)', background: 'var(--card-bg)', border: '0.5px solid var(--border)', padding: '4px 8px', borderRadius: 6, opacity: 0.9 }}>
              Scroll or pinch to zoom into Metro Atlanta · drag to pan
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-header">
          <div className="card-title">Approved service counties · {approvedList.length}</div>
        </div>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {approvedList.map(name => {
            const v = valueFor(name);
            return (
              <button
                key={name}
                onClick={() => openCounty(name)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font)',
                  fontSize: 12.5, fontWeight: 600, padding: '6px 10px 6px 12px', borderRadius: 999,
                  background: 'var(--success-bg)', color: 'var(--success)',
                  border: '0.5px solid var(--success-border)', cursor: 'pointer',
                }}
              >
                {name}
                <span style={{
                  fontSize: 10.5, fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                  background: v > 0 ? 'var(--success)' : 'var(--bg2)', color: v > 0 ? '#fff' : 'var(--text3)',
                  borderRadius: 999, minWidth: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                }}>
                  {v}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {unmatched.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--text3)' }}>
          {unmatched.length} client{unmatched.length === 1 ? '' : 's'} have a county on file that doesn't match a Georgia county
          ({[...new Set(unmatched.map(u => u.county))].join(', ')}) — not shown on the map.
        </div>
      )}
      {showLeads && unmatchedLeads.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--text3)' }}>
          {unmatchedLeads.length} lead{unmatchedLeads.length === 1 ? '' : 's'} have a county on file that doesn't match a Georgia county
          ({[...new Set(unmatchedLeads.map(u => u.county))].join(', ')}) — not shown on the map.
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
        County boundaries: US Census Bureau. County seats: official Georgia county records. Counts reflect current client records.
      </div>

      {/* Drawer */}
      <div
        onClick={closeDrawer}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(6,13,24,0.4)', zIndex: 40,
          opacity: selectedCounty ? 1 : 0, pointerEvents: selectedCounty ? 'auto' : 'none', transition: 'opacity 0.18s ease',
        }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 380, maxWidth: '92vw',
        background: 'var(--card-bg)', borderLeft: '0.5px solid var(--border)', boxShadow: '-12px 0 32px rgba(10,22,40,0.18)',
        transform: selectedCounty ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.22s cubic-bezier(.2,.9,.3,1)',
        zIndex: 41, display: 'flex', flexDirection: 'column',
      }}>
        {selectedCounty && (
          <>
            <div style={{ padding: '18px 44px 14px 18px', borderBottom: '0.5px solid var(--border)', position: 'relative' }}>
              <button onClick={closeDrawer} aria-label="Close" style={{
                position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: 8,
                border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer', fontSize: 15, lineHeight: 1,
              }}>×</button>
              <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)', fontWeight: 600, marginBottom: 4 }}>County</div>
              <div style={{ fontSize: 19, fontWeight: 700 }}>{selectedCounty} County</div>
              <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>Seat: {selectedGeo?.seat || '—'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 18px', borderBottom: '0.5px solid var(--border)' }}>
              <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg2)', borderRadius: 8, padding: '8px 4px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{selected?.total ?? 0}</div>
                <div style={{ fontSize: 9.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>Total</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg2)', borderRadius: 8, padding: '8px 4px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{selected?.active ?? 0}</div>
                <div style={{ fontSize: 9.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>Active</div>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '6px 10px 18px' }}>
              {selectedList.length === 0 && (
                <div style={{ padding: '16px 8px', color: 'var(--text3)', fontSize: 13 }}>No active clients in this county.</div>
              )}
              {selectedList.map(cl => (
                <div
                  key={cl.client_id}
                  onClick={() => navigate(`/clients/${cl.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    padding: '10px 8px', borderRadius: 8, cursor: 'pointer', border: '0.5px solid transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{cl.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`badge ${ZONE_BADGE_CLASS[cl.zone]}`}>{cl.status}</span>
                    <span style={{ color: 'var(--text3)', fontSize: 13 }}>→</span>
                  </div>
                </div>
              ))}

              {selectedLeads.length > 0 && (
                <>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 7, margin: '14px 8px 6px',
                    fontSize: 10.5, fontWeight: 600, color: 'var(--lead-marker)', textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--lead-marker)' }} />
                    Leads · {selectedLeads.length}
                  </div>
                  {selectedLeads.map(l => (
                    <div
                      key={l.lead_id}
                      onClick={() => navigate(`/leads/${l.id}`)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                        padding: '10px 8px', borderRadius: 8, cursor: 'pointer', border: '0.5px solid transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lead-marker-bg)'; e.currentTarget.style.borderColor = 'var(--lead-marker-border)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
                    >
                      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{l.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: 'var(--lead-marker-bg)', color: 'var(--lead-marker)' }}>{l.status}</span>
                        <span style={{ color: 'var(--text3)', fontSize: 13 }}>→</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
